import { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, RefreshCw, FileText } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import { useJSAs } from '@/hooks/use-safety';
import { formatDateOnly } from '@/lib/date-utils';
import type { JSAStatus } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';

const statusColors: Record<JSAStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_signatures: 'bg-yellow-100 text-yellow-800',
  active: 'bg-green-100 text-green-800',
  closed: 'bg-blue-100 text-blue-800',
  archived: 'bg-gray-100 text-gray-600',
};

const statusLabels: Record<JSAStatus, string> = {
  draft: 'Draft',
  pending_signatures: 'Pending Signatures',
  active: 'Active',
  closed: 'Closed',
  archived: 'Archived',
};

const PAGE_SIZE = 20;

export function JSAsPage() {
  const { hasMinModuleRole } = usePermissions();
  const canManageSafety = hasMinModuleRole('safety', 'manager');
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');

  const page = parseInt(searchParams.get('page') || '1', 10);
  const status = (searchParams.get('status') || '') as JSAStatus | '';
  const q = searchParams.get('q') || '';

  const { data, isLoading, isError, refetch } = useJSAs({
    page,
    page_size: PAGE_SIZE,
    q,
    status,
  });

  const jsas = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(() => {
    updateSearchParams({ q: searchInput, page: '1' });
  }, [searchInput, updateSearchParams]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Project Safety Analyses</h1>
          <p className="text-gray-600 mt-1">
            Manage JSAs for your projects
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {canManageSafety && (
            <Button asChild>
              <Link to="/safety/jsas/new">
                <Plus className="h-4 w-4 mr-2" />
                New JSA
              </Link>
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search JSAs..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-10"
              />
            </div>
            <Select
              value={status}
              onValueChange={(value) =>
                updateSearchParams({ status: value === 'all' ? '' : value })
              }
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_signatures">Pending Signatures</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch}>Search</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load JSAs. Please try again.
            </div>
          ) : jsas.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600 font-medium">
                {q || status ? 'No JSAs match your search' : 'No JSAs yet'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {q || status
                  ? 'Try adjusting your search or filters'
                  : 'Create your first JSA to start managing safety on your projects'}
              </p>
              {canManageSafety && (
                <Button asChild className="mt-4">
                  <Link to="/safety/jsas/new">
                    <Plus className="h-4 w-4 mr-2" />
                    Create your first JSA
                  </Link>
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jsas.map((jsa) => (
                  <TableRow key={jsa.id}>
                    <TableCell>
                      <Link
                        to={`/safety/jsas/${jsa.id}`}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {jsa.title}
                      </Link>
                      {jsa.project_name && (
                        <p className="text-sm text-gray-500">{jsa.project_name}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-600">{jsa.location}</TableCell>
                    <TableCell className="text-gray-600">
                      {formatDateOnly(jsa.date)}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[jsa.status]}>
                        {statusLabels[jsa.status]}
                      </Badge>
                      {jsa.rescue_plan_override_reason && (
                        <Badge className="ml-2 bg-red-100 text-red-800 hover:bg-red-100">No rescue plan</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-600">{jsa.created_by_username}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => updateSearchParams({ page: String(Math.max(1, page - 1)) })}
                className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    isActive={page === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                onClick={() =>
                  updateSearchParams({ page: String(Math.min(totalPages, page + 1)) })
                }
                className={
                  page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
